import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import BrandHeader from '../../components/common/BrandHeader';
import PageLoader from '../../components/common/PageLoader';
import ClinicianCard from '../../components/common/ClinicianCard';
import OutlineButton from '../../components/common/OutlineButton';
import PrimaryButton from '../../components/common/PrimaryButton';
import {
  Card,
  CardBody,
  HelpText,
  PageHead,
  PageShell,
  PageSubtitle,
  PageTitle,
  SectionTitle,
} from '../../components/common/Card';
import CancelModal from './CancelModal';
import PostponeModal from './PostponeModal';
import useQueryData from '../../hooks/useQueryData';
import useWorkflowData from '../../hooks/useWorkflowData';
import { getLoadedWorkflow } from '../../slices/workflow';
import { ROUTE_PREFIX_BOOKING, ROUTES } from '../../constants/routes';
import { APPOINTMENT_TYPE, STATUS } from '../../constants';
import { CONTACT_PHONE } from '../../constants/business';
import { generateName } from '../../utils';
import { generateAppointmentType } from '../../utils/bookingUtils';
import {
  removeLegacySmsTemplateId,
  saveSmsTemplateId,
} from '../../utils/smsTemplateStorage';
import analytics from '../../infra/analytics';
import { AltRow, HeroImage, OrDivider, StrongSpan } from './styles';

const LandingPage = () => {
  const history = useHistory();
  const { workflowId } = useParams();
  const { smsTemplateId } = useQueryData();
  const workflowData = useSelector(({ workflow }) =>
    getLoadedWorkflow(workflow, workflowId)
  );
  const workflowFetchingStatus = useSelector(({ workflow }) => workflow.status);
  const { client, specialist, appointment } = workflowData;

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isPostponeModalOpen, setIsPostponeModalOpen] = useState(false);

  useEffect(() => {
    analytics.logPageViewEvent('landing', {
      workflow_id: workflowId,
    });
  }, [workflowId]);

  useEffect(() => {
    removeLegacySmsTemplateId();
    saveSmsTemplateId(workflowId, smsTemplateId);
  }, [smsTemplateId, workflowId]);

  useWorkflowData();

  if (workflowFetchingStatus === STATUS.FAILURE) {
    throw new Error('Can not fetch workflow data');
  }

  const handleBook = useCallback(() => {
    history.push(`${ROUTE_PREFIX_BOOKING}/${workflowId}${ROUTES.SELECT_DATE}`);
  }, [history, workflowId]);

  const toggleCancelModal = useCallback(
    (isOpen) => setIsCancelModalOpen(isOpen),
    []
  );

  const togglePostponeModal = useCallback(
    (isOpen) => setIsPostponeModalOpen(isOpen),
    []
  );

  const renderAppointmentSummary = () => {
    const appointmentType = generateAppointmentType(
      appointment?.isNewAppointment
    );
    const clinicianSection = (
      <StrongSpan>
        {`Dr. ${generateName(specialist)}`}
        {specialist?.speciality ? ` ( ${specialist.speciality} )` : ''}
      </StrongSpan>
    );

    if (appointmentType === APPOINTMENT_TYPE.NEW) {
      return (
        <>
          You are due for a{' '}
          <StrongSpan>{APPOINTMENT_TYPE.NEW} appointment</StrongSpan> with{' '}
          {clinicianSection}
        </>
      );
    }

    const dueWeeks =
      appointment?.dueWeeks && appointment.dueWeeks > 0
        ? appointment.dueWeeks
        : 0;

    return (
      <>
        You are due for a review with {clinicianSection} in{' '}
        <StrongSpan>{dueWeeks} weeks</StrongSpan>
      </>
    );
  };

  if (isEmpty(workflowData)) {
    return (
      <>
        <BrandHeader />
        <PageLoader />
      </>
    );
  }

  return (
    <>
      <BrandHeader />
      <PageShell>
        <PageHead>
          <PageTitle>Hi {generateName(client)}!</PageTitle>
          <PageSubtitle>{renderAppointmentSummary()}</PageSubtitle>
        </PageHead>

        <Card>
          <CardBody>
            <ClinicianCard specialist={specialist} />
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <SectionTitle>
              Please select one of the following options
            </SectionTitle>

            <PrimaryButton onClick={handleBook}>Book</PrimaryButton>

            <OrDivider>Or</OrDivider>

            <AltRow>
              <OutlineButton onClick={() => togglePostponeModal(true)}>
                Postpone
              </OutlineButton>
              <OutlineButton onClick={() => toggleCancelModal(true)}>
                Cancel
              </OutlineButton>
            </AltRow>
          </CardBody>
        </Card>

        <HeroImage>
          <picture>
            <source
              srcSet={`${process.env.PUBLIC_URL}/assets/hero-image.webp`}
              type="image/webp"
            />
            <img
              src={`${process.env.PUBLIC_URL}/assets/hero-image.svg`}
              alt=""
            />
          </picture>
        </HeroImage>

        <HelpText>
          For further information please press the chat icon or call{' '}
          <b>{CONTACT_PHONE}</b> to speak with us.
        </HelpText>

        <CancelModal
          isCancelModalOpen={isCancelModalOpen}
          toggleCancelModal={toggleCancelModal}
        />
        <PostponeModal
          isPostponeModalOpen={isPostponeModalOpen}
          togglePostponeModal={togglePostponeModal}
        />
      </PageShell>
    </>
  );
};

export default LandingPage;
